import React from 'react';
import { string, number, bool, func, node, oneOfType } from 'prop-types';
import { InlineTextButton } from '../../components';
import classNames from 'classnames';

import css from './ActionButton.module.css';

/**
 * ActionButton component provides a consistent UI element for action buttons
 * throughout the application. Can be rendered as either a button or a div.
 *
 * @param {Object} props - Component props
 * @returns {React.ReactElement} The ActionButton component
 */
const ActionButton = ({
  children,
  rootClassName = null,
  className = null,
  label = null,
  disabled = false,
  isButton = true,
  onClick = () => {},
  ariaLabel = null,
}) => {
  const classes = classNames(rootClassName || css.root, className, {
    [css.disabled]: disabled,
  });

  // Determine accessible name from either ariaLabel prop or label content
  const accessibleName = ariaLabel || label;

  // Common props for both button and div variants
  const commonProps = {
    className: classes,
    onClick: disabled ? undefined : onClick,
    'aria-disabled': disabled,
  };

  // If a label is present, add the wrapper span
  const labelElement = label != null ? <span className={css.label}>{label}</span> : null;

  if (isButton) {
    return (
      <InlineTextButton
        {...commonProps}
        type="button"
        aria-label={accessibleName}
        disabled={disabled}
      >
        {children}
        {labelElement}
      </InlineTextButton>
    );
  }

  // Not a button: a plain wrapper that groups its own controls (the vote buttons) or shows a
  // count, so it is neither focusable nor a button itself (axe: nested-interactive)
  return (
    <div className={classes}>
      {children}
      {labelElement}
    </div>
  );
};

ActionButton.propTypes = {
  // Styling props
  rootClassName: string,
  className: string,

  // Content props
  children: node,
  label: oneOfType([string, number]),
  ariaLabel: string,

  // Behavior props
  disabled: bool,
  isButton: bool,
  onClick: func,
};

export default ActionButton;
